
import React, { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {Menu, X, User, ChevronDown, LogOut} from 'lucide-react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const navigate = useNavigate()
  const user = lumi.auth.user
  const dropdownRef = useRef<HTMLDivElement>(null)

  const toggleMenu = () => setIsOpen(!isOpen)
  const toggleDropdown = () => setDropdownOpen(!dropdownOpen)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleDashboardClick = () => {
    setDropdownOpen(false)
    if (user?.userRole === 'superadmin') {
      navigate('/dashboard/superadmin')
    } else if (user?.userRole === 'admin' || user?.userRole === 'admin_regional') {
      navigate('/dashboard/admin')
    } else if (user?.userRole === 'recruiter') {
      navigate('/dashboard/recruiter')
    } else if (user?.userRole === 'affiliate') {
      navigate('/dashboard/affiliate')
    } else {
      navigate('/dashboard/nurse')
    }
  }

  const handleSignOut = () => {
    lumi.auth.signOut()
    setDropdownOpen(false)
    toast.success('Signed out successfully')
    navigate('/')
  }

  return (
    <nav className="bg-white border-b border-gray-50 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3">
          {/* Logo and Brand */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img 
              src="https://i.imghippo.com/files/KBd1355nw.png"
              alt="Nursing Hive logo"
              className="w-[58px] h-auto object-contain group-hover:scale-105 transition-transform duration-200"
            />
            <span className="text-xl font-bold text-primary-navy group-hover:text-primary-teal transition-colors duration-200">
              Nursing Hive
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className="text-primary-navy hover:text-primary-teal transition-all duration-150 hover:border-b-2 hover:border-primary-teal hover:pb-0.5"
            >
              Home
            </Link>
            <Link 
              to="/about" 
              className="text-primary-navy hover:text-primary-teal transition-all duration-150 hover:border-b-2 hover:border-primary-teal hover:pb-0.5"
            >
              About
            </Link>
            <Link 
              to="/countries" 
              className="text-primary-navy hover:text-primary-teal transition-all duration-150 hover:border-b-2 hover:border-primary-teal hover:pb-0.5"
            >
              Destinations
            </Link>
            <Link 
              to="/resources" 
              className="text-primary-navy hover:text-primary-teal transition-all duration-150 hover:border-b-2 hover:border-primary-teal hover:pb-0.5"
            >
              Resources
            </Link>
            <Link 
              to="/recruiters" 
              className="text-primary-navy hover:text-primary-teal transition-all duration-150 hover:border-b-2 hover:border-primary-teal hover:pb-0.5"
            >
              Recruiters
            </Link>
            <Link 
              to="/affiliates" 
              className="text-primary-navy hover:text-primary-teal transition-all duration-150 hover:border-b-2 hover:border-primary-teal hover:pb-0.5"
            >
              Affiliates
            </Link>
            <Link 
              to="/faqs" 
              className="text-primary-navy hover:text-primary-teal transition-all duration-150 hover:border-b-2 hover:border-primary-teal hover:pb-0.5"
            >
              FAQ
            </Link>
            <Link 
              to="/contact" 
              className="text-primary-navy hover:text-primary-teal transition-all duration-150 hover:border-b-2 hover:border-primary-teal hover:pb-0.5"
            >
              Contact
            </Link>
            {user && (
              <Link 
                to={
                  user.userRole === 'superadmin' ? '/dashboard/superadmin' :
                  user.userRole === 'admin' ? '/dashboard/admin' :
                  user.userRole === 'admin_regional' ? '/dashboard/admin' :
                  user.userRole === 'recruiter' ? '/dashboard/recruiter' :
                  user.userRole === 'affiliate' ? '/dashboard/affiliate' :
                  '/dashboard/nurse'
                }
                className="text-primary-navy hover:text-primary-teal transition-all duration-150 hover:border-b-2 hover:border-primary-teal hover:pb-0.5 font-semibold"
              >
                Dashboard
              </Link>
            )}
          </div>

          {/* CTA Button / User Menu */}
          <div className="hidden md:block relative" ref={dropdownRef}>
            {user ? (
              <>
                <button
                  onClick={toggleDropdown}
                  className="flex items-center gap-2 bg-primary-teal text-white px-[18px] py-[10px] rounded-lg font-semibold hover:bg-teal-700 transition-all duration-200"
                >
                  <User className="w-4 h-4" />
                  <span>{user.userName || user.email?.split('@')[0] || 'User'}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <button
                      onClick={handleDashboardClick}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <User className="w-4 h-4" />
                      Dashboard
                    </button>
                    <button
                      onClick={handleSignOut}
                      className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                )}
              </>
            ) : (
              <Link
                to="/signup"
                className="bg-primary-teal text-white px-[18px] py-[10px] rounded-lg font-semibold hover:bg-teal-700 hover:scale-[1.03] transition-all duration-200"
              >
                Register Free / Sign In
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={toggleMenu}
              className="text-primary-navy hover:text-primary-teal transition-colors"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <>
            <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={toggleMenu}></div>
            <div className="md:hidden bg-white border-t border-gray-100 absolute left-0 right-0 top-full shadow-lg z-50 animate-in slide-in-from-top-2 duration-200">
              <div className="px-4 py-4 space-y-4">
                <Link
                  to="/"
                  className="block text-primary-navy hover:text-primary-teal transition-colors text-lg font-semibold"
                  onClick={toggleMenu}
                >
                  Home
                </Link>
                <Link
                  to="/about"
                  className="block text-primary-navy hover:text-primary-teal transition-colors text-lg font-semibold"
                  onClick={toggleMenu}
                >
                  About
                </Link>
                <Link
                  to="/countries"
                  className="block text-primary-navy hover:text-primary-teal transition-colors text-lg font-semibold"
                  onClick={toggleMenu}
                >
                  Destinations
                </Link>
                <Link
                  to="/resources"
                  className="block text-primary-navy hover:text-primary-teal transition-colors text-lg font-semibold"
                  onClick={toggleMenu}
                >
                  Resources
                </Link>
                <Link
                  to="/recruiters"
                  className="block text-primary-navy hover:text-primary-teal transition-colors text-lg font-semibold"
                  onClick={toggleMenu}
                >
                  Recruiters
                </Link>
                <Link
                  to="/affiliates"
                  className="block text-primary-navy hover:text-primary-teal transition-colors text-lg font-semibold"
                  onClick={toggleMenu}
                >
                  Affiliates
                </Link>
                <Link
                  to="/faqs"
                  className="block text-primary-navy hover:text-primary-teal transition-colors text-lg font-semibold"
                  onClick={toggleMenu}
                >
                  FAQ
                </Link>
                <Link
                  to="/contact"
                  className="block text-primary-navy hover:text-primary-teal transition-colors text-lg font-semibold"
                  onClick={toggleMenu}
                >
                  Contact
                </Link>
                {user && (
                  <Link
                    to={
                      user.userRole === 'superadmin' ? '/dashboard/superadmin' :
                      user.userRole === 'admin' ? '/dashboard/admin' :
                      user.userRole === 'admin_regional' ? '/dashboard/admin' :
                      user.userRole === 'recruiter' ? '/dashboard/recruiter' :
                      user.userRole === 'affiliate' ? '/dashboard/affiliate' :
                      '/dashboard/nurse'
                    }
                    className="block text-primary-navy hover:text-primary-teal transition-colors text-lg font-semibold"
                    onClick={toggleMenu}
                  >
                    Dashboard
                  </Link>
                )}
                <div className="pt-2">
                  {user ? (
                    <button
                      onClick={() => { toggleMenu(); handleDashboardClick(); }}
                      className="w-full flex items-center justify-center gap-2 bg-primary-teal text-white px-4 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
                    >
                      <User className="w-4 h-4" />
                      <span>{user.userName || user.email?.split('@')[0] || 'Dashboard'}</span>
                    </button>
                  ) : (
                    <Link
                      to="/signup"
                      className="block bg-primary-teal text-white px-4 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors text-center"
                      onClick={toggleMenu}
                    >
                      Register Free / Sign In
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </nav>
  )
}

export default Navbar
